import { useState } from "react";
import { siteInfo } from "@/mocks/comics";
import SectionHeading from "./SectionHeading";

const SUBMIT_ADDR = "https://readdy.ai/api/form/da2kb4aamq5jpe1hrur0";

export default function Contact() {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = String(fd.get("website_alt") || "").trim();
    setSubmitting(true);
    setFormError("");
    setSuccess(false);

    // anti-spam: silently treat as success
    if (honeypot) {
      setSuccess(true);
      form.reset();
      setSubmitting(false);
      return;
    }

    fd.delete("website_alt");

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (res.ok && parsed?.code === "OK") {
        setSuccess(true);
        form.reset();
      } else {
        setFormError(
          typeof msg === "string" && msg.includes("spam")
            ? "提交内容被拦截，请勿填写无关信息后重试。"
            : typeof msg === "string" && msg.trim()
              ? msg
              : "提交失败，请稍后重试。",
        );
      }
    } catch {
      setFormError("网络异常，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-14 md:py-20 bg-background-50">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading
          eyebrow="Contact 联系我们"
          title="有想推荐的好漫？欢迎告诉我们"
          desc="无论是作品投稿推荐、合作建议还是意见反馈，都欢迎通过下方表单与我们联系。"
        />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8 items-start">
          {/* info */}
          <div className="lg:col-span-2 space-y-4">
            <div className="p-5 rounded-2xl bg-background-100 border border-background-200">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-500 text-background-50">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <div>
                  <div className="text-sm font-bold text-foreground-950">合作与投稿</div>
                  <a href={`mailto:${siteInfo.contactEmail}`} className="text-sm text-primary-600 hover:text-primary-700">
                    {siteInfo.contactEmail}
                  </a>
                </div>
              </div>
              <p className="text-xs text-foreground-500 mt-3 leading-relaxed">
                投稿推荐冷门佳作、商务合作或媒体邀约，均可发送邮件，我们会在工作日尽快回复。
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-secondary-100/70 border border-secondary-200">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-time-line text-secondary-700 text-lg"></i>
                <span className="text-sm font-bold text-foreground-950">回复时效</span>
              </div>
              <ul className="space-y-2 text-xs md:text-sm text-foreground-700 leading-relaxed">
                <li className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-secondary-600"></i>
                  榜单更新：每周一
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-secondary-600"></i>
                  投稿回复：3 个工作日内
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-secondary-600"></i>
                  意见反馈：1 个工作日内
                </li>
              </ul>
            </div>
          </div>

          {/* form */}
          <div className="lg:col-span-3">
            <form
              onSubmit={handleSubmit}
              data-readdy-form="漫潮联系咨询表单"
              className="p-5 md:p-7 rounded-3xl bg-background-100 border border-background-200 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="name" className="text-sm font-semibold text-foreground-800">您的称呼</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼您"
                    className="px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="email" className="text-sm font-semibold text-foreground-800">邮箱地址</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    className="px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="topic" className="text-sm font-semibold text-foreground-800">咨询主题</label>
                <select
                  id="topic"
                  name="topic"
                  className="px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                >
                  <option value="作品投稿推荐">作品投稿推荐</option>
                  <option value="榜单问题反馈">榜单问题反馈</option>
                  <option value="商务合作">商务合作</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="message" className="text-sm font-semibold text-foreground-800">留言内容</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  placeholder="请简要描述您的需求或想推荐的作品（500 字以内）"
                  className="px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-none"
                ></textarea>
              </div>

              {/* anti-spam honeypot */}
              <div className="form-hp" aria-hidden="true">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              {formError && (
                <p className="text-sm text-primary-700 bg-primary-100/60 border border-primary-200 rounded-xl px-4 py-3">
                  {formError}
                </p>
              )}
              {success && (
                <p className="text-sm text-secondary-800 bg-secondary-100 border border-secondary-200 rounded-xl px-4 py-3 flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-secondary-600"></i>
                  提交成功，感谢您的反馈，我们会尽快与您联系！
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    提交留言
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}